import { z } from '@customerio/hydra-validation';
import type { CustomForm as UIAPICustomForm } from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';
import type { Segment } from '../segment/entity';
import { hasDuplicateTrimmedValue } from '../shared/validation';

/**
 * Custom form (workspace-scoped) — third-party forms (Typeform / Jotform /
 * scanned HTML / API) connected to Customer.io. The integrations directory
 * reads only the form-type + status predicates below.
 */
export type CustomForm = UIAPICustomForm;

/** Form type discriminants (subset the directory reads). */
export const CustomFormType = {
  custom: 'custom',
  typeform: 'typeform',
  jotform: 'jotform',
} as const;

/** Connection status values. */
export const CustomFormStatus = {
  draft: 'draft',
  connected: 'connected',
  disconnected: 'disconnected',
} as const;

export const CustomFormError = {
  missingIdentifier: 'missing_identifier',
  multipleIdentifiers: 'multiple_identifiers',
  invalidEmailIdentifier: 'invalid_email_identifier',
  dataDrivenSegmentLimitReached: 'data_driven_segment_limit_reached',
} as const;

const RESERVED_FORM_FIELD_NAMES = new Set(['form_id', 'form_name', 'form_type', 'form_url', 'form_url_params']);

const CustomFormFromApi = z
  .looseObject({
    id: z.string(),
  })
  .pipe(z.custom<CustomForm>());

/** @public */
export const customForm = defineEntity<CustomForm, CustomForm, void, undefined, 'workspace'>({
  name: 'custom_form',
  scope: 'workspace',
  pk: (f) => f.id,
  schema: { read: CustomFormFromApi },
});

export const customFormKeys = customForm.keys;

export function parseCustomForm(raw: UIAPICustomForm): CustomForm {
  return customForm.parse(raw);
}

type FormPredicateInput = Pick<CustomForm, 'form_type' | 'api_form_id' | 'html_form_inputs'>;

/** `custom-form.isConnectedOrDraft` — a connected or draft form. */
export function isConnectedOrDraftForm(form: Pick<CustomForm, 'status'>): boolean {
  return form.status === CustomFormStatus.connected || form.status === CustomFormStatus.draft;
}

export function isDraftCustomForm(form: Pick<CustomForm, 'status'>): boolean {
  return form.status === CustomFormStatus.draft;
}

export function isConnectedCustomForm(form: Pick<CustomForm, 'status'>): boolean {
  return form.status === CustomFormStatus.connected;
}

export function isDisconnectedCustomForm(form: Pick<CustomForm, 'status'>): boolean {
  return form.status === CustomFormStatus.disconnected;
}

/** `custom-form.isApiForm` — a custom form with an API form id. */
export function isApiForm(form: FormPredicateInput): boolean {
  return form.form_type === CustomFormType.custom && form.api_form_id !== '';
}

/** `custom-form.isScannedForm` — a custom form with scanned HTML inputs. */
export function isScannedForm(form: FormPredicateInput): boolean {
  return form.form_type === CustomFormType.custom && (form.html_form_inputs?.length ?? 0) > 0;
}

/** A custom form that posts through the Customer.io HTML action endpoint. */
export function isHtmlActionForm(form: FormPredicateInput): boolean {
  return form.form_type === CustomFormType.custom && !isApiForm(form) && !isScannedForm(form);
}

/** `custom-form.isTypeform` */
export function isTypeform(form: Pick<CustomForm, 'form_type'>): boolean {
  return form.form_type === CustomFormType.typeform;
}

/** `custom-form.isJotform` */
export function isJotform(form: Pick<CustomForm, 'form_type'>): boolean {
  return form.form_type === CustomFormType.jotform;
}

export function customFormHasError(form: Pick<CustomForm, 'error'>): boolean {
  return form.error !== '';
}

export function customFormHasErrorOfType(
  form: Pick<CustomForm, 'error'>,
  error: (typeof CustomFormError)[keyof typeof CustomFormError],
): boolean {
  return form.error === error;
}

export function customFormValue(form: Pick<CustomForm, 'form_id'>): string {
  return form.form_id;
}

export function customFormLabel(form: Pick<CustomForm, 'name'>): string {
  return form.name;
}

export function customFormIdLowercase(form: Pick<CustomForm, 'form_id'>): string {
  return form.form_id.toLowerCase();
}

/** Whether Ember Data treated the persisted record as a newly created form. */
export function isNewCustomForm(
  form: Pick<CustomForm, 'form_type' | 'api_form_id' | 'created_at' | 'updated_at'>,
): boolean {
  if (form.updated_at !== form.created_at) {
    return false;
  }
  return (form.form_type === CustomFormType.custom && form.api_form_id !== '') || isTypeform(form);
}

export function hasDuplicateCustomFormFieldKey(form: Pick<CustomForm, 'attr_maps'>): boolean {
  return hasDuplicateTrimmedValue(form.attr_maps.map(({ form_field_key }) => form_field_key));
}

export function hasDuplicateCustomFormFieldMapping(form: Pick<CustomForm, 'attr_maps'>): boolean {
  return hasDuplicateTrimmedValue(form.attr_maps.map(({ cio_attr }) => cio_attr));
}

export function hasEmptyCustomFormFieldMapping(form: Pick<CustomForm, 'attr_maps'>): boolean {
  return form.attr_maps.some(({ form_field_key, cio_attr }) => form_field_key.trim() === '' || cio_attr.trim() === '');
}

export function hasReservedCustomFormFieldMapping(form: Pick<CustomForm, 'attr_maps'>): boolean {
  return form.attr_maps.some(({ form_field_key }) => RESERVED_FORM_FIELD_NAMES.has(form_field_key.trim()));
}

export function isReservedCustomFormFieldName(value: string): boolean {
  return RESERVED_FORM_FIELD_NAMES.has(value.trim());
}

export function hasCustomFormCioIdMapping(form: Pick<CustomForm, 'attr_maps'>): boolean {
  return form.attr_maps.some(({ cio_attr }) => cio_attr === 'cio_id');
}

export function customFormHasExactlyOneIdentifierMapping(
  form: Pick<CustomForm, 'attr_maps'>,
  enabledIdentifiers: readonly string[],
): boolean {
  return form.attr_maps.filter(({ cio_attr }) => enabledIdentifiers.includes(cio_attr)).length === 1;
}

/** Validation used by the saved-form editor, including its API-form-only required mappings. */
export function hasInvalidCustomFormDetailsMapping(
  form: Pick<CustomForm, 'form_type' | 'api_form_id' | 'html_form_inputs' | 'attr_maps'>,
  enabledIdentifiers: readonly string[],
): boolean {
  return (
    hasDuplicateCustomFormFieldKey(form) ||
    hasDuplicateCustomFormFieldMapping(form) ||
    hasReservedCustomFormFieldMapping(form) ||
    hasCustomFormCioIdMapping(form) ||
    !customFormHasExactlyOneIdentifierMapping(form, enabledIdentifiers) ||
    (isApiForm(form) && hasEmptyCustomFormFieldMapping(form))
  );
}

export function customFormIdentifierAttrMaps(
  form: Pick<CustomForm, 'attr_maps'>,
  enabledIdentifiers: readonly string[],
): CustomForm['attr_maps'] {
  return form.attr_maps.filter(({ cio_attr }) => enabledIdentifiers.includes(cio_attr));
}

export function customFormSegment<T extends Pick<Segment, 'linked_form_id'>>(
  form: Pick<CustomForm, 'form_id'>,
  segments: readonly T[],
): T | undefined {
  return segments.find((segment) => segment.linked_form_id === form.form_id);
}

export function customFormManageUrl(form: Pick<CustomForm, 'form_type' | 'form_id'>): string | undefined {
  if (isTypeform(form)) {
    return `https://admin.typeform.com/form/${form.form_id}/create`;
  }
  if (isJotform(form)) {
    return `https://jotform.com/build/${form.form_id}`;
  }
  return undefined;
}
